import React, { useCallback, createContext, useState, useEffect } from 'react';
import serialize from 'utils/serialize';
import countWithNull from 'utils/countWithNull';
import { useSelector } from 'react-redux';
import {
  getTasks,
  getTaskDetail,
  getDocumentTypes,
  postValidateDocument,
  postRejectDocument,
  postCreateCarrier,
  fetcher,
} from './http';
import useSWR from 'swr';
import { SLOW_POLLING_DELAY } from './const';

const DocsContext = createContext();

function DocsContextProvider({ children }) {
  const [error, setError] = useState(false);
  const [counters, setCounters] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [loadingTaskDetail, setLoadingTaskDetail] = useState(false);
  const [documentTypes, setDocumentTypes] = useState(null);
  const [loadingDocTypes, setLoadingDocTypes] = useState(false);
  const me = useSelector((state) => state.me);
  const [filters, setFilters] = useState({ country: me.country });
  const resetState = useCallback(() => {
    setError(false);
    setLoadingDocTypes(false);
  }, []);

  const {
    data: tasks,
    error: tasksError,
    mutate: refreshTasks,
  } = useSWR(getTasks(serialize(filters || {})), fetcher, {
    refreshInterval: SLOW_POLLING_DELAY,
  });

  const fetchTaskDetail = useCallback(async (docId) => {
    try {
      setLoadingTaskDetail(true);
      setError(false);
      const data = await getTaskDetail(docId);
      setTaskDetail(data);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoadingTaskDetail(false);
    }
  }, []);

  const fetchDocumentTypes = useCallback(async () => {
    try {
      setLoadingDocTypes(true);
      setError(false);
      const data = await getDocumentTypes();
      setDocumentTypes(data);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoadingDocTypes(false);
    }
  }, []);

  useEffect(() => {
    if (tasks && tasks.length > 0 && me?.email) {
      const ownerList = tasks.map((document) => document.entity.document_owner);
      const ownerCounters = countWithNull(ownerList, me.email);
      setCounters({
        unowned: ownerCounters.null,
        me: ownerCounters.match,
        all: ownerCounters.total,
      });
    }
  }, [tasks, me.email]);

  return (
    <DocsContext.Provider
      value={{
        loadingTasks: !tasks,
        error,
        tasks,
        counters,
        loadingTaskDetail,
        fetchTaskDetail,
        taskDetail,
        loadingDocTypes,
        documentTypes,
        fetchDocumentTypes,
        postRejectDocument,
        postValidateDocument,
        postCreateCarrier,
        resetState,
        refreshTasks,
        setFilters,
        filters,
        tasksError,
      }}
    >
      {children}
    </DocsContext.Provider>
  );
}

export { DocsContextProvider };
export default DocsContext;
